import { Component } from 'react'
import { QueryResult } from '@apollo/client'
import Query from '~/components/Query'
import withSchemeServiceAreas, {
  SERVICE_AREAS_GEOJSON,
} from '~/components/Scheme/withSchemeServiceAreas'
import Map, { ViewState, BasisMapProps } from '~/components/Map'
import {
  ServiceAreaGeoJsonLayer,
  ZoneIconLayers,
  VehicleIconLayer,
} from '~/components/Map/layers'

import { LocationAreaInput } from '../../__generated__/globalTypes'
import { SCHEME } from '~/types'
import Legend, { LegendProps } from '~/components/Map/Legend'
import {
  Vehicle,
  VehicleMapDocument,
  VehicleMapQuery,
} from '~/gql/generated/graphql'

type MapProps = {
  id: string
  scheme: SCHEME
  serviceAreas: SERVICE_AREAS_GEOJSON
}

type MapState = {
  legendProps: LegendProps | null
  bounds: LocationAreaInput | null
}

class VehicleMap extends Component<MapProps, MapState> {
  state: MapState = {
    bounds: null,
    legendProps: null,
  }

  _setLegend = (legendProps: MapState['legendProps']) => {
    this.setState({
      legendProps,
    })
  }

  _onViewStateChange = (viewState: ViewState) => {
    const bounds = {
      longitude_min: viewState.bounds._ne.lng,
      longitude_max: viewState.bounds._sw.lng,
      latitude_min: viewState.bounds._ne.lat,
      latitude_max: viewState.bounds._sw.lat,
    }
    this.setState({ bounds })
  }

  render() {
    const { id, serviceAreas, scheme } = this.props
    const { legendProps, bounds } = this.state

    return (
      <Query
        showEmpty={false}
        waitFor="data.vehicle_by_module_id"
        query={VehicleMapDocument}
        variables={{
          id,
          location: bounds,
          schemeId: scheme?.id,
          hasLocation: bounds !== null,
          lacksLocation: bounds === null,
          showSuspendedBays: !!scheme?.id,
        }}
      >
        {({ data, loading, previousData }: QueryResult<VehicleMapQuery>) => {
          const mapProps: Partial<BasisMapProps> = { layers: [] }

          if (loading && previousData) {
            // hack: without this we get a flash of a blank
            // map when ever a query is inflight.
            // I tried notifyOnNetworkStatusChange = false
            // first but it didn't seem to work.
            data = previousData
          }
          const location = data?.vehicle_by_module_id?.detail?.module?.location
          if (location) {
            mapProps.latitude = location?.latitude
            mapProps.longitude = location?.longitude
            const vehicle = data?.vehicle_by_module_id as Vehicle
            const vehicles =
              data?.vehicles?.nodes.filter(m => m.id !== id) ?? []

            mapProps.layers = [
              ServiceAreaGeoJsonLayer({
                serviceAreas,
                layerId: 'service-area-layer',
                layerProps: {
                  onClick: ({ object: { properties } }) =>
                    this._setLegend({
                      type: 'ServiceArea',
                      service_area: properties,
                    }),
                },
              }),
              ...ZoneIconLayers({
                zones: data?.all_zones?.nodes ?? [],
                layerId: 'zone-layer',
                iconLayerProps: {
                  onClick: ({ object }) =>
                    this._setLegend({
                      type: 'Zone',
                      zone_id: object.id,
                    }),
                },
              }),
              VehicleIconLayer({
                vehicles,
                layerId: 'vehicles-icon-layer',
                layerProps: {
                  getSize: 33,
                  onClick: ({ object }) =>
                    this._setLegend({
                      type: 'Vehicle',
                      module_id: object?.detail?.module?.id,
                    }),
                },
              }),
              VehicleIconLayer({
                vehicles: [vehicle],
                layerId: 'vehicle-icon-layer',
                layerProps: {
                  onClick: ({ object }) =>
                    this._setLegend({
                      type: 'Vehicle',
                      module_id: object?.detail?.module?.id,
                    }),
                },
                includeNonPresentModules: true,
              }),
            ]
          }

          return (
            <Map
              {...mapProps}
              message="Could not find module location"
              onViewStateChange={this._onViewStateChange}
            >
              <Legend
                onClose={() => this._setLegend(null)}
                {...(legendProps as LegendProps)}
              />
            </Map>
          )
        }}
      </Query>
    )
  }
}

export default withSchemeServiceAreas(VehicleMap)
